import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { useIdleReveal } from '../hooks/useIdleReveal';
import { computeTooltipPosition } from '../utils/tooltipPosition';

const Anchor = styled.span`
  display: inline-flex;
  vertical-align: top;
`;

const Bubble = styled.div`
  position: fixed;
  z-index: 99999;
  box-sizing: border-box;
  background: #1a1a1a;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.45);
  max-width: ${p => p.$maxWidth || 'none'};
  white-space: ${p => (p.$maxWidth ? 'normal' : 'nowrap')};
  opacity: ${p => (p.$open ? 1 : 0)};
  pointer-events: ${p => (p.$open && p.$interactive ? 'auto' : 'none')};
  cursor: ${p => (p.$interactive ? 'pointer' : 'default')};

  @media (prefers-reduced-motion: no-preference) {
    transition: opacity 0.18s ease;
    ${p => (p.$pulse && p.$open ? 'animation: tooltipTipPulse 2.4s ease-in-out infinite;' : '')}
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    animation: none;
  }

  @keyframes tooltipTipPulse {
    0%, 100% { transform: translateX(0); opacity: 1; }
    50% { transform: translateX(-3px); opacity: 0.93; }
  }

  @media print { display: none !important; }
`;

const Arrow = styled.span`
  position: absolute;
  width: 0;
  height: 0;
  pointer-events: none;

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    border-style: solid;
  }

  &[data-placement='left'] {
    top: var(--arrow-offset);
    right: 0;
    transform: translate(100%, -50%);
    &::before {
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      border-width: 9px 0 9px 9px;
      border-color: transparent transparent transparent rgba(255, 255, 255, 0.14);
    }
    &::after {
      left: 0;
      top: 50%;
      transform: translateY(-50%) translateX(-2px);
      border-width: 8px 0 8px 8px;
      border-color: transparent transparent transparent #1a1a1a;
    }
  }

  &[data-placement='right'] {
    top: var(--arrow-offset);
    left: 0;
    transform: translate(-100%, -50%);
    &::before {
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      border-width: 9px 9px 9px 0;
      border-color: transparent rgba(255, 255, 255, 0.14) transparent transparent;
    }
    &::after {
      right: 0;
      top: 50%;
      transform: translateY(-50%) translateX(2px);
      border-width: 8px 8px 8px 0;
      border-color: transparent #1a1a1a transparent transparent;
    }
  }

  &[data-placement='top'] {
    left: var(--arrow-offset);
    bottom: 0;
    transform: translate(-50%, 100%);
    &::before {
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      border-width: 9px 9px 0 9px;
      border-color: rgba(255, 255, 255, 0.14) transparent transparent transparent;
    }
    &::after {
      top: 0;
      left: 50%;
      transform: translateX(-50%) translateY(-2px);
      border-width: 8px 8px 0 8px;
      border-color: #1a1a1a transparent transparent transparent;
    }
  }

  &[data-placement='bottom'] {
    left: var(--arrow-offset);
    top: 0;
    transform: translate(-50%, -100%);
    &::before {
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      border-width: 0 9px 9px 9px;
      border-color: transparent transparent rgba(255, 255, 255, 0.14) transparent;
    }
    &::after {
      bottom: 0;
      left: 50%;
      transform: translateX(-50%) translateY(2px);
      border-width: 0 8px 8px 8px;
      border-color: transparent transparent #1a1a1a transparent;
    }
  }
`;

const DismissButton = styled.button`
  display: block;
  margin-top: 6px;
  padding: 0;
  border: none;
  background: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover { color: #fff; }
`;

function isCoarsePointer() {
  return typeof window !== 'undefined' && window.matchMedia?.('(hover: none)')?.matches;
}

function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
}

export default function Tooltip({
  id,
  text,
  children,
  trigger = 'idle',
  idleDelay = 7000,
  placement = 'top',
  offset = 10,
  maxWidth,
  arrow = false,
  dismissLabel,
  closeLabel,
  persistDismiss = 'session',
  priority = 0,
  interactive = false,
  open: openProp,
  onOpenChange,
  onDismiss,
  onClick,
}) {
  const reactId = useId();
  const tooltipDomId = `tooltip-${id || reactId}`;
  const anchorRef = useRef(null);
  const bubbleRef = useRef(null);
  const [coords, setCoords] = useState(null);

  const { open, setOpen, dismiss } = useIdleReveal({
    id,
    trigger,
    idleDelay,
    persistDismiss: dismissLabel ? persistDismiss : 'none',
    priority,
    open: openProp,
    onOpenChange,
    onDismiss,
  });

  const [mounted, setMounted] = useState(open);
  useEffect(() => {
    if (open) {
      setMounted(true);
      return undefined;
    }
    if (prefersReducedMotion()) {
      setMounted(false);
      return undefined;
    }
    const timer = setTimeout(() => setMounted(false), 180);
    return () => clearTimeout(timer);
  }, [open]);

  const updatePosition = useCallback(() => {
    const anchorEl = anchorRef.current;
    const bubbleEl = bubbleRef.current;
    if (!anchorEl || !bubbleEl) return;
    const next = computeTooltipPosition({
      anchorRect: anchorEl.getBoundingClientRect(),
      bubbleRect: { width: bubbleEl.offsetWidth, height: bubbleEl.offsetHeight },
      viewport: { width: window.innerWidth, height: window.innerHeight },
      placement,
      offset,
      padding: 8,
    });
    setCoords(next);
  }, [placement, offset]);

  useLayoutEffect(() => {
    if (!mounted) return undefined;
    updatePosition();
    let raf = 0;
    const requestUpdate = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        updatePosition();
      });
    };
    window.addEventListener('resize', requestUpdate);
    window.addEventListener('scroll', requestUpdate, true);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', requestUpdate);
      window.removeEventListener('scroll', requestUpdate, true);
    };
  }, [mounted, updatePosition, text]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  useEffect(() => {
    if (trigger !== 'hover' || !open || !isCoarsePointer()) return undefined;
    const onPointerDown = (event) => {
      if (anchorRef.current?.contains(event.target)) return;
      if (bubbleRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [trigger, open, setOpen]);

  const handleAnchorEnter = () => {
    if (trigger === 'hover') setOpen(true);
  };
  const handleAnchorLeave = () => {
    if (trigger === 'hover') setOpen(false);
  };
  const handleAnchorFocus = () => {
    if (trigger === 'hover' && isCoarsePointer()) setOpen(true);
  };
  const handleAnchorBlur = () => {
    if (trigger === 'hover' && isCoarsePointer()) setOpen(false);
  };
  const handleAnchorClick = (event) => {
    if (trigger === 'hover' && isCoarsePointer()) {
      event.preventDefault();
      setOpen(!open);
    }
  };

  const handleBubbleClick = (event) => {
    if (!interactive) return;
    onClick?.(event);
  };

  const handleDismiss = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dismiss();
  };

  if (text == null || text === '') {
    return children;
  }

  const bubbleTree = mounted && typeof document !== 'undefined' ? createPortal(
    <Bubble
      ref={bubbleRef}
      id={tooltipDomId}
      role="tooltip"
      $open={open}
      $interactive={interactive}
      $pulse={arrow && trigger === 'idle'}
      $maxWidth={maxWidth}
      onClick={handleBubbleClick}
      style={{
        top: coords ? coords.top : 0,
        left: coords ? coords.left : 0,
        visibility: coords ? 'visible' : 'hidden',
        '--arrow-offset': `${coords?.arrowOffset ?? 0}px`,
      }}
    >
      {text}
      {dismissLabel && (
        <DismissButton
          type="button"
          onClick={handleDismiss}
          aria-label={closeLabel || dismissLabel}
        >
          {dismissLabel}
        </DismissButton>
      )}
      {arrow && (
        <Arrow data-placement={coords?.placement || placement} aria-hidden />
      )}
    </Bubble>,
    document.body,
  ) : null;

  return (
    <Anchor
      ref={anchorRef}
      aria-describedby={open ? tooltipDomId : undefined}
      onMouseEnter={handleAnchorEnter}
      onMouseLeave={handleAnchorLeave}
      onFocus={handleAnchorFocus}
      onBlur={handleAnchorBlur}
      onClick={handleAnchorClick}
    >
      {children}
      {bubbleTree}
    </Anchor>
  );
}
